import { Fragment, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, DisabledIcon } from '../../../icons'
import { productModelsDeletePermissions, productModelsWritePermissions } from '../../../utils/permissionValidation'
import { clearProductModelsMessages, loadAdminProductTypes, removeAdminProductTypes } from '../../../actions/productModels'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'
import AddEditProductType from './AddEditProductType'
import ViewProductType from './ViewProductType'

const orderingFields = [
  {
    field: 'productType',
    order: 'asc',
    translationKey: 'product_type_asc',
  },
  {
    field: 'productType',
    order: 'desc',
    translationKey: 'product_type_desc',
  },
  {
    field: 'category',
    order: 'asc',
    translationKey: 'category_asc',
  },
  {
    field: 'category',
    order: 'desc',
    translationKey: 'category_desc',
  },
  {
    field: 'deviceFunction',
    order: 'asc',
    translationKey: 'device_function_asc',
  },
  {
    field: 'deviceFunction',
    order: 'desc',
    translationKey: 'device_function_desc',
  },
  {
    field: 'brand',
    order: 'asc',
    translationKey: 'brand_asc',
  },
  {
    field: 'brand',
    order: 'desc',
    translationKey: 'brand_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const ProductTypes = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [productTypesTableData, setProductTypesTableData] = useState([])
  const [selectedFilterProductType, setSelectedFilterProductType] = useState(null)
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null)
  const [selectedFilterDeviceFunction, setSelectedFilterDeviceFunction] = useState(null)
  const [selectedBrandFilter, setSelectedBrandFilter] = useState([])
  const [statusesFilter, setStatusesFilter] = useState([])
  const [selectedTypeDetails, setSelectedTypeDetails] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedVariant, setExpandedVariant] = useState(0)
  const [showAddEditProductTypeModal, setShowAddEditProductTypeModal] = useState(false)
  const [showViewProductTypeModal, setShowViewProductTypeModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [typeIdToDelete, setTypeIdToDelete] = useState(null)
  const Statuses = ['active', 'inactive']

  const toId = (map, selectedName) => {
    if (!selectedName) return null
    return map.get(selectedName) ?? null
  }

  const toIds = (map, selectedNames = []) => selectedNames.map((n) => map.get(n)).filter((id) => id != null)

  const brandLabels = props.brands?.map((b) => {
    return b.brand || []
  })
  const categoryLabels = props.productCategories?.map((pc) => {
    return pc.category || []
  })
  const productTypeLabels = props.productTypes?.map((pt) => {
    return pt.productType || []
  })
  const deviceFunctionLabels = props.deviceFunctions?.map((df) => {
    return df.deviceFunction || []
  })

  const brandNameToId = useMemo(() => {
    const map = new Map()
    ;(props.brands || []).forEach((b) => {
      if (b?.brand && b?.id != null) map.set(b.brand, b.id)
    })
    return map
  }, [props.brands])

  const categoryNameToId = useMemo(() => {
    const map = new Map()
    ;(props.productCategories || []).forEach((c) => {
      const name = c?.category || c?.name
      if (name && c?.id != null) map.set(name, c.id)
    })
    return map
  }, [props.productCategories])

  const productTypeNameToId = useMemo(() => {
    const map = new Map()
    ;(props.productTypes || []).forEach((pt) => {
      const name = pt?.productType || pt?.name
      if (name && pt?.id != null) map.set(name, pt.id)
    })
    return map
  }, [props.productTypes])

  const deviceFunctionNameToId = useMemo(() => {
    const map = new Map()
    ;(props.deviceFunctions || []).forEach((df) => {
      const name = df?.deviceFunction || df?.name
      if (name && df?.id != null) map.set(name, df.id)
    })
    return map
  }, [props.deviceFunctions])

  useEffect(() => {
    const tableData = props.productTypesData?.map((item, index) => {
      return {
        index,
        _original: item,
        productType: item.productType || '',
        category: item.category?.name || '',
        deviceFunction: item.deviceFunction?.name || '',
        brand: item.brand?.name || '',
        status: item.isActive ? Statuses[0] : Statuses[1] || '',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item, true)
            },
            requiredPermission: productModelsWritePermissions,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(item)
            },
            requiredPermission: productModelsDeletePermissions,
            isDisabled: item.isActive,
          },
        ],
      }
    })

    setProductTypesTableData(tableData)
  }, [props.productTypesData])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.errorMessage, props.actions])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)
      props.actions.clearProductModelsMessages()
    }
  }, [props.successMessage, props.actions])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    const changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadTableData(true)
        }
        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterProductType, selectedFilterCategory, statusesFilter, selectedFilterDeviceFunction, selectedBrandFilter, orderBy])

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }
    props.actions.loadAdminProductTypes(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterProductType(null)
    setSelectedFilterCategory(null)
    setSelectedFilterDeviceFunction(null)
    setSelectedBrandFilter([])
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) queryParams = { ...queryParams, query: searchParam }

    if (selectedFilterProductType) {
      queryParams = { ...queryParams, productType: selectedFilterProductType }
    }

    if (selectedFilterCategory) {
      queryParams = { ...queryParams, categoryId: toId(categoryNameToId, selectedFilterCategory) }
    }

    if (selectedFilterDeviceFunction) {
      queryParams = { ...queryParams, deviceFunctionId: toId(deviceFunctionNameToId, selectedFilterDeviceFunction) }
    }

    if (selectedBrandFilter.length > 0) {
      queryParams = { ...queryParams, brandId: toIds(brandNameToId, selectedBrandFilter) }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === Statuses[0] }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEdit = (typeDetails, isEditModal) => {
    setSelectedTypeDetails(typeDetails)
    isEditModal ? setShowAddEditProductTypeModal(true) : setShowViewProductTypeModal(true)
  }

  const showConfirmationModal = (item) => {
    setTypeIdToDelete(item)
    setShowDeleteConfirmModal(true)
  }

  const handleDeleteType = () => {
    setShowDeleteConfirmModal(false)
    props.actions.removeAdminProductTypes(typeIdToDelete?.id)
  }

  const closeModal = () => {
    props.actions.clearProductModelsMessages()
    setSelectedTypeDetails(null)
    setShowAddEditProductTypeModal(false)
    setShowViewProductTypeModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderStatus = (status) => {
    switch (status) {
      case Statuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case Statuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper flex-justify-between">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={
              selectedFilterProductType || selectedFilterCategory || selectedFilterDeviceFunction || statusesFilter.length > 0 || selectedBrandFilter.length > 0
            }
            counterBadge={
              (selectedFilterProductType && 1) +
              (selectedFilterCategory && 1) +
              (selectedFilterDeviceFunction && 1) +
              statusesFilter.length +
              selectedBrandFilter.length
            }
            minSearchLength={2}
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditProductTypeModal(true)} requiredPermission={productModelsWritePermissions}>
            {t('product_models_context.add_new_type')}
          </SimpleButton>
        </div>

        {isMobile && productTypesTableData?.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}

        {!isMobile && productTypesTableData?.length > 0 && (
          <GenericTable
            data={productTypesTableData}
            headers={[
              {
                title: t('product_type'),
                orderKey: 'productType',
              },
              {
                title: t('category'),
                orderKey: 'category',
              },
              {
                title: t('product_models_context.device_function'),
                orderKey: 'deviceFunction',
              },
              {
                title: t('dealer-portal.brand'),
                orderKey: 'brand',
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['productType', 'category', 'deviceFunction', 'brand', 'status', 'action']}
            keyRenderer={{
              status: (item) => {
                return <div className="status-wrapper">{renderStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(item) => handleOpenViewEdit(item._original)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}

        {isMobile &&
          productTypesTableData?.map((item, index) => (
            <div key={index} className="card" onClick={() => setExpandedVariant(index)}>
              <div className={'card-item' + (expandedVariant !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('product_type')}</div>
                <div className={'card-item-body' + (expandedVariant !== index ? ' align-center' : '')}>
                  <div>{item.productType}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedVariant === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('category')}</div>
                    <div className="card-item-body">{item.category}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('product_models_context.device_function')}</div>
                    <div className="card-item-body">{item.deviceFunction}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('dealer-portal.brand')}</div>
                    <div className="card-item-body">{item.brand}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderStatus(item.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}

        {!showSpinner && !isFirstLoad && productTypesTableData?.length === 0 && (
          <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
        )}

        {canLoadMore && productTypesTableData.length > 0 && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('product_type'),
              type: FilterType.singleSelect,
              dataset: productTypeLabels,
              input: selectedFilterProductType,
              output: (type) => {
                setSelectedFilterProductType(type)
              },
            },
            {
              name: t('category'),
              type: FilterType.singleSelect,
              dataset: categoryLabels,
              input: selectedFilterCategory,
              output: (category) => {
                setSelectedFilterCategory(category)
              },
            },
            {
              name: t('product_models_context.device_function'),
              type: FilterType.singleSelect,
              dataset: deviceFunctionLabels,
              input: selectedFilterDeviceFunction,
              output: (deviceFunction) => {
                setSelectedFilterDeviceFunction(deviceFunction)
              },
            },
            {
              name: t('dealer-portal.brand'),
              type: FilterType.multiSelect,
              dataset: brandLabels,
              input: selectedBrandFilter,
              output: (brand) => {
                setSelectedBrandFilter(brand)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: Statuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}

      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}

      {showAddEditProductTypeModal && (
        <AddEditProductType typeDetails={selectedTypeDetails} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}

      {showViewProductTypeModal && selectedTypeDetails && (
        <ViewProductType
          typeDetails={selectedTypeDetails}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditType={() => {
            setShowViewProductTypeModal(false)
            setShowAddEditProductTypeModal(true)
          }}
          closeModal={closeModal}
        />
      )}

      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={() => handleDeleteType()}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('product_models_context.delete_model_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={productModelsDeletePermissions}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ productModels }) {
  return {
    productTypesData: productModels?.productTypes || [],
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    productCategories: productModels?.productModelsFilterOptions?.productCategories || [],
    brands: productModels?.productModelsFilterOptions?.brands || [],
    deviceFunctions: productModels?.productModelsFilterOptions?.deviceFunctions || [],
    isLoading: productModels?.isLoading,
    canLoadMore: productModels?.canLoadMore,
    successMessage: productModels?.successMessage,
    errorMessage: productModels?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadAdminProductTypes,
        removeAdminProductTypes,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ProductTypes)
